import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useSearchParams } from "react-router-dom";
import { Badge, Button } from "reactstrap";
import { Popover, Whisper } from "rsuite";

import {
  brieMenuOpenAtom,
  brieMenuPageAtom,
} from "../../BrieChatMenu/BrieChatMenuButton";
import {
  BrieGPTChat,
  useBrieGPTChats,
  useBrieGPTProjects,
} from "./useBrieGPTItems";
import { BrieGPTTabNavigator } from "./BrieGPTSidebarElements/BrieGPTTabNavigator";
import DropdownOptionWithIcon from "../../Menus/DropdownOptionWithIcon";
import { AddButton } from "../../Sidebar/SidebarAdd/SidebarAdd";
import SkeletonLoader from "../../../elements/SkeletonLoader";
import { BrieGPTTabs } from "./BrieGPTSidebarElements/Types";
import ProjectSettingsDrawer from "./ProjectSettingsDrawer";
import { headerOpenAtom } from "../../../atoms/PageAtoms";
import useUpdateBrieGPTUser from "./useUpdateBrieGPTUser";
import { newToastEvent } from "../../../utils/ToastUtil";
import useDeleteGPTProject from "./useDeleteGPTProject";
import ChatSettingsDrawer from "./ChatSettingsDrawer";
import useDeleteGPTChat from "./useDeleteGPTChat";
import NewProjectModal from "./NewProjectModal";
import useHover from "../../../hooks/useHover";
import StrUtils from "../../../utils/StrUtils";
import ProjectOptions from "./ProjectOptions";
import { IsMobileAtom } from "../../../App";
import useGPTUser from "./useBrieGPTUser";

export type BrieTypeProps =
  | { type: "view"; viewID: string }
  | { type: "record"; recordID: string; moduleAPIName: string };

type Props = BasicProps & BrieTypeProps;

interface BasicProps {
  chatID: string | null;
  processing: boolean;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  tab: BrieGPTTabs;
  setTab: Dispatch<SetStateAction<BrieGPTTabs>>;
}

export default function BrieGPTSidebar({
  chatID,
  processing,
  open,
  setOpen,
  tab,
  setTab,
  ...typeProps
}: Props) {
  const [feedbackOpen, setFeedbackOpen] = useAtom(brieMenuOpenAtom);
  const setFeedbackPage = useSetAtom(brieMenuPageAtom);
  const { projects, projectsLoading } = useBrieGPTProjects({
    tab: tab,
    ...typeProps,
  });
  const { chats: c, chatsLoading } = useBrieGPTChats(typeProps);

  const [params, setParams] = useSearchParams();
  const projectID = params.get("projectID");
  const { deleteChat, deletingChat } = useDeleteGPTChat(projectID, tab);
  const { deleteProject, deletingProject } = useDeleteGPTProject(tab);
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [editProjectID, setEditProjectID] = useState<string | null>(null);
  const [editChatID, setEditChatID] = useState<string | null>(null);
  const headerOpen = useAtomValue(headerOpenAtom);
  const isMobile = useAtomValue(IsMobileAtom);

  // GPT User hooks
  const { gptUser } = useGPTUser();
  const { updateGPTUser } = useUpdateBrieGPTUser();

  const pinnedChatsIDs = useMemo(() => {
    return gptUser?.PinnedChats?.map((chat: any) => chat?.Chat?.id) || [];
  }, [gptUser?.PinnedChats]);

  // Stores the sorted chats
  const chats = useMemo(() => {
    if (!c || !Array.isArray(c)) return [];

    // Just return the chats if there are no pinned chats.
    if (!pinnedChatsIDs.length) return c;

    return c.reduce((sortingChats, chat) => {
      // Moving the chat to the top if it's pinned.
      if (pinnedChatsIDs.includes(chat.id)) {
        sortingChats.unshift(chat);
      } else {
        // No pinned chat, just push it to the end.
        sortingChats.push(chat);
      }

      return sortingChats;
    }, [] as BrieGPTChat[]);
  }, [c, pinnedChatsIDs]);

  if (!open) {
    return (
      <div
        className="tw:bg-superlight tw:h-fit tw:-mt-2 tw:z-10 tw:px-2 tw:py-1 tw:fixed tw:md:static tw:left-0 btn-oauth cursor-pointer"
        onClick={() => setOpen(true)}
      >
        <i className="mdi mdi-chevron-right tw:text-dark tw:text-2xl" />
      </div>
    );
  }

  return (
    <>
      <div
        className={`tw:bg-superlight tw:-mt-2 tw:-mb-8 tw:-ml-8 tw:md:ml-0 tw:z-10 tw:absolute tw:md:static tw:w-full tw:md:w-72 ${
          headerOpen
            ? "tw:h-[calc(100vh_-_208px)]"
            : "tw:h-[calc(100vh_-_160px)]"
        }`}
      >
        <div className="tw:flex tw:justify-end">
          <i
            className="mdi mdi-chevron-left tw:cursor-pointer tw:text-2xl tw:pt-1 tw:pr-2"
            onClick={() => setOpen(false)}
          />
        </div>
        <div className="tw:m-2 tw:bg-white tw:rounded tw:sm:min-h-64">
          <BrieGPTTitle />
          <BrieGPTTabNavigator tab={tab} setTab={setTab} />
          <div
            className={`tw:overflow-y-auto tw:overflow-x-hidden tw:px-2 tw:pb-6 ${
              headerOpen
                ? "tw:max-h-[calc(100vh_-_368px)]"
                : "tw:max-h-[calc(100vh_-_320px)]"
            }`}
          >
            <BrieSidebarHeader
              title="Projects"
              onClick={() => setNewProjectOpen(true)}
              showAdd={projects && projects?.length > 0}
            />
            {!projectsLoading && !projects?.length && (
              <BrieNewButton
                title="New Project"
                onClick={() => setNewProjectOpen(true)}
                disabled={processing}
              />
            )}
            {projectsLoading && (
              <>
                <SkeletonLoader height={28} />
                <SkeletonLoader height={28} />
              </>
            )}
            {projects?.map((p) => {
              return (
                <BrieSidebarProject
                  key={p.id}
                  title={p.Name}
                  id={p.id}
                  chats={p?.Chats}
                  deleteChat={({ chatID }) =>
                    deleteChat({ chatID: chatID, ...typeProps })
                  }
                  deletingChat={deletingChat}
                  deleteProject={deleteProject}
                  deletingProject={deletingProject}
                  editProjectID={editProjectID}
                  setEditProjectID={setEditProjectID}
                  editChatID={editChatID}
                  setEditChatID={setEditChatID}
                  onSidebarItemClick={(chatID) =>
                    onSidebarItemClick(p.id, chatID)
                  }
                  pinnedChatsIDs={pinnedChatsIDs}
                  onPin={(chat) => handlePinChat(chat)}
                  {...typeProps}
                />
              );
            })}
            {tab !== "Team" && (
              <>
                <BrieSidebarHeader
                  title="Chats"
                  onClick={() => {
                    params.delete("projectID");
                    params.delete("chatID");
                    setParams(params);
                  }}
                  showAdd={chats && chats?.length > 0}
                />
                {chatsLoading && (
                  <>
                    <SkeletonLoader height={28} />
                    <SkeletonLoader height={28} />
                    <SkeletonLoader height={28} />
                    <SkeletonLoader height={28} />
                  </>
                )}
                {!chatsLoading && !chatID && !projectID && (
                  <BrieSidebarItem
                    title="New Chat"
                    id={chatID}
                    onDelete={() => {}}
                    deletingChat={deletingChat}
                    editChatID={editChatID}
                    setEditChatID={setEditChatID}
                    onClick={(chatID) => onSidebarItemClick(null, chatID)}
                    isSelected={!chatID}
                  />
                )}
                {chats?.map((chat: any) => (
                  <BrieSidebarItem
                    key={chat.id}
                    title={chat.Name}
                    id={chat.id}
                    onDelete={() =>
                      deleteChat({ chatID: chat.id, ...typeProps })
                    }
                    deletingChat={deletingChat}
                    editChatID={editChatID}
                    setEditChatID={setEditChatID}
                    onClick={(chatID) => onSidebarItemClick(null, chatID)}
                    isSelected={chat.id === chatID}
                    pinned={pinnedChatsIDs.includes(chat.id)}
                    onPin={() => handlePinChat(chat)}
                  />
                ))}
              </>
            )}
          </div>
        </div>
        <div className="tw:w-full tw:px-2">
          <Button
            size="sm"
            color="white"
            className="tw:w-full tw:text-dark btn-oauth"
            onClick={() => {
              if (feedbackOpen) {
                setFeedbackOpen(false);
                setTimeout(() => setFeedbackOpen(true), 100);
              } else {
                setFeedbackOpen(true);
              }
              setFeedbackPage("chat-feedback");
            }}
          >
            Give Feedback
          </Button>
        </div>
      </div>
      <NewProjectModal
        open={newProjectOpen}
        onClose={() => setNewProjectOpen(false)}
        tab={tab}
        {...typeProps}
      />
      <ProjectSettingsDrawer
        open={!!editProjectID}
        onClose={() => setEditProjectID(null)}
        projectID={editProjectID}
        tab={tab}
        {...typeProps}
      />
      <ChatSettingsDrawer
        open={!!editChatID}
        onClose={() => setEditChatID(null)}
        chatID={editChatID}
        projectID={projectID}
        tab={tab}
        {...typeProps}
      />
    </>
  );

  function onSidebarItemClick(pID: string | null, id: string | null) {
    if (processing) {
      return newToastEvent(
        "error",
        "Can't switch chats while BrieGPT is processing."
      );
    }

    if (isMobile) setOpen(false);

    if (!id) {
      params.delete("chatID");
    } else {
      params.set("chatID", id);
    }

    if (pID) params.set("projectID", pID);
    else params.delete("projectID");

    setParams(params);
  }

  function handlePinChat(chat: BrieGPTChat) {
    updateGPTUser({
      payload: {
        PinnedChat: {
          id: StrUtils.uuidv4(),
          Chat: {
            id: chat.id,
            _RecordName: chat.Name,
            _ModuleAPIName: "BrieGPTChat",
          },
        },
      },
    });
  }
}

export function BrieGPTTitle() {
  return (
    <h2 className="tw:border-b tw:border-b-superlight tw:flex tw:items-center tw:justify-between tw:p-2">
      Brie GPT <Badge color="primary">Beta</Badge>
    </h2>
  );
}

export function BrieNewButton({
  disabled,
  onClick,
  title,
}: {
  disabled: boolean;
  onClick: () => void;
  title: string;
}) {
  return (
    <Button
      color="primary"
      className="tw:w-full tw:h-8 tw:flex tw:justify-center tw:items-center tw:text-sm tw:mb-4 tw:max-w-52"
      disabled={disabled}
      onClick={onClick}
    >
      {title}
      <i className="mdi mdi-plus-circle tw:pl-2" />
    </Button>
  );
}

export function BrieSidebarHeader({
  title,
  onClick,
  showAdd,
}: {
  title: string;
  onClick: () => void;
  showAdd?: boolean;
}) {
  return (
    <div className="tw:flex tw:justify-between tw:items-center tw:mt-3 tw:h-10">
      <h4 className="tw:text-light tw:uppercase tw:font-semibold tw:mb-0">
        {title}
      </h4>
      {showAdd && (
        <AddButton active={false} onClick={onClick} disabled={false} open />
      )}
    </div>
  );
}

export function BrieSidebarProject({
  title,
  id,
  chats,
  deleteChat,
  deletingChat,
  deleteProject,
  deletingProject,
  editProjectID,
  setEditProjectID,
  editChatID,
  setEditChatID,
  onSidebarItemClick,
  pinnedChatsIDs,
  onPin,
  ...typeProps
}: {
  title: string;
  id: string;
  chats: BrieGPTChat[];
  deleteChat: ({ chatID }: { chatID: string } & BrieTypeProps) => void;
  deletingChat: boolean;
  deleteProject: ({
    projectID,
  }: {
    projectID: string;
  } & BrieTypeProps) => void;
  deletingProject: boolean;
  editProjectID: string | null;
  setEditProjectID: Dispatch<SetStateAction<string | null>>;
  editChatID: string | null;
  setEditChatID: Dispatch<SetStateAction<string | null>>;
  onSidebarItemClick: (newChatID: string | null) => void;
  pinnedChatsIDs: string[];
  onPin?: (chat: BrieGPTChat) => void;
} & BrieTypeProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [hoverRef, isHovered] = useHover<HTMLDivElement>(popoverOpen);
  const [params, setParams] = useSearchParams();
  const projectID = params.get("projectID");
  const chatID = params.get("chatID");
  const projectOpen = projectID === id;
  const isSelected = projectOpen && !chatID;

  const projectChats = useMemo(() => {
    if (!chats?.length) return [];

    return chats.reduce((sortingChats, chat) => {
      // Moving the chat to the top if it's pinned.
      if (pinnedChatsIDs.includes(chat.id)) {
        sortingChats.unshift(chat);
      } else {
        // No pinned chat, just push it to the end.
        sortingChats.push(chat);
      }

      return sortingChats;
    }, [] as BrieGPTChat[]);
  }, [chats, pinnedChatsIDs]);

  return (
    <>
      <div
        title={title}
        ref={hoverRef}
        className={`tw:flex tw:justify-between tw:items-center tw:hover:bg-ultralight tw:cursor-pointer tw:py-1 tw:px-2 tw:rounded ${
          isSelected ? "tw:bg-primary-light-bg" : ""
        }`}
        onClick={() => {
          params.set("projectID", id);
          params.delete("chatID");
          setParams(params);
        }}
      >
        <div className="tw:flex tw:items-center tw:w-full">
          <i
            className={`mdi tw:pr-2 ${
              projectOpen ? "mdi-folder-open-outline" : "mdi-folder-outline"
            }`}
          />
          <div
            className={`tw:text-sm tw:font-medium tw:truncate tw:flex-1 tw:select-none ${
              isSelected ? "tw:text-dark" : ""
            }`}
          >
            {title}
          </div>
          {isHovered && id && (
            <ProjectOptions
              popoverOpen={popoverOpen}
              setPopoverOpen={setPopoverOpen}
              onOpenSettings={() => setEditProjectID(id)}
              onDelete={() => {
                deleteProject({ projectID: id, ...typeProps });
              }}
              deleting={deletingProject}
              hide={!!editProjectID}
            />
          )}
        </div>
      </div>
      {projectOpen &&
        projectChats?.map((chat: any) => {
          return (
            <BrieSidebarItem
              title={chat?.Name}
              id={chat?.id}
              onDelete={() => deleteChat({ chatID: chat.id, ...typeProps })}
              deletingChat={deletingChat}
              className="tw:ml-6"
              editChatID={editChatID}
              setEditChatID={setEditChatID}
              onClick={() => onSidebarItemClick(chat.id)}
              isSelected={chat.id === chatID}
              pinned={pinnedChatsIDs.includes(chat.id)}
              onPin={onPin ? () => onPin(chat) : undefined}
            />
          );
        })}
    </>
  );
}
export function BrieSidebarItem({
  title,
  id,
  onDelete,
  deletingChat,
  className,
  isPrivate,
  editChatID,
  setEditChatID,
  isSelected,
  onClick,
  pinned,
  onPin,
}: {
  title: string;
  id: string | null;
  onDelete: () => void;
  deletingChat: boolean;
  className?: string;
  isPrivate?: boolean;
  editChatID: string | null;
  setEditChatID: Dispatch<SetStateAction<string | null>>;
  isSelected: boolean;
  onClick: (newChatID: string | null) => void;
  pinned?: boolean;
  onPin?: () => void;
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [hoverRef, isHovered] = useHover<HTMLDivElement>(popoverOpen);

  return (
    <div
      title={title}
      className={`tw:flex tw:justify-between tw:items-center tw:hover:bg-ultralight tw:cursor-pointer tw:py-1 tw:px-2 tw:rounded ${
        isSelected ? "bg-primary-selected tw:text-dark" : ""
      } ${className ? className : ""}`}
      ref={hoverRef}
      onClick={() => onClick(id)}
    >
      <div className="tw:flex tw:items-center tw:w-full">
        <i
          className={`mdi ${
            isPrivate ? "mdi-message-lock-outline" : "mdi-message-text-outline"
          } tw:pr-2`}
        />
        <div className="tw:text-sm tw:font-medium tw:truncate tw:flex-1 tw:select-none">
          {title}
        </div>
        {pinned && (
          <i
            className={`mdi mdi-pin-off tw:px-1 tw:cursor-pointer ${
              isHovered ? "tw:mr-3" : ""
            } `}
            onClick={(e) => {
              e.stopPropagation();
              onPin && onPin();
            }}
            style={{
              color: "var(--primary)",
            }}
          />
        )}
        {isHovered && id && (
          <ChatOptions
            id={id}
            onDelete={onDelete}
            onPin={onPin}
            pinned={pinned}
            setEditChatID={setEditChatID}
            editChatID={editChatID}
            deletingChat={deletingChat}
            popoverOpen={popoverOpen}
            setPopoverOpen={setPopoverOpen}
          />
        )}
      </div>
    </div>
  );
}

export function ChatOptions({
  editChatID,
  setEditChatID,
  id,
  onDelete,
  onPin,
  pinned,
  deletingChat,
  setPopoverOpen,
  popoverOpen,
}: {
  editChatID: string | null;
  setEditChatID: Dispatch<SetStateAction<string | null>>;
  onDelete: () => void;
  deletingChat: boolean;
  pinned?: boolean;
  onPin?: () => void;
  id: string;
  setPopoverOpen: Dispatch<SetStateAction<boolean>>;
  popoverOpen: boolean;
}) {
  return (
    <Whisper
      trigger="click"
      onEntering={() => setPopoverOpen(true)}
      onClose={() => setPopoverOpen(false)}
      placement="autoVerticalStart"
      preventOverflow
      speaker={
        <Popover
          className={`sidebar-popover ${editChatID ? "tw:hidden" : ""}`}
          full
          onClick={(e: any) => {
            e.stopPropagation();
          }}
        >
          <DropdownOptionWithIcon
            name="Chat Settings"
            onClick={() => setEditChatID(id)}
            icon="mdi mdi-cog"
            disabled={false}
          />
          <DropdownOptionWithIcon
            name="Delete"
            onClick={onDelete}
            icon="mdi mdi-delete"
            disabled={deletingChat}
          />
          {onPin && (
            <DropdownOptionWithIcon
              name={pinned ? "Unpin" : "Pin"}
              onClick={(e) => {
                e.stopPropagation();
                onPin();
                setPopoverOpen(false);
              }}
              icon={pinned ? "mdi mdi-pin-off" : "mdi mdi-pin"}
              disabled={false}
            />
          )}
        </Popover>
      }
    >
      <i
        className={`mdi mdi-dots-horizontal tw:cursor-pointer tw:px-1 tw:rounded tw:text-lg tw:leading-5 tw:bg-ultralight tw:hover:bg-white tw:hover:outline tw:hover:outline-gray-200 ${
          popoverOpen ? "tw:outline tw:outline-gray-200" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      />
    </Whisper>
  );
}
